import { DecisionConfidenceCompact, DecisionMissingInformation, DecisionRiskCompact, DecisionState } from "../../models/home";
import { LanguageService } from "../language/language.service";
import type {
  DecisionConfidenceDisplay, DecisionConfidenceTone, DecisionMissingInformationDisplay,
  DecisionRiskDisplay, DecisionRiskTone, MissingInformationResolutionLocation
} from "./home-decision-display-ux.service";

export class HomeDecisionRiskDisplayBuilder {
  constructor(private readonly lang: LanguageService) {}

  getRiskDisplay(risk: DecisionRiskCompact, state: DecisionState): DecisionRiskDisplay {
    const tone = this.getRiskTone(risk, state);
    const reason = this.normalizeUserFacingReason(risk.reason);
    const summary = reason && !this.looksTechnical(reason)
      ? reason
      : this.lang.translate(`home.decisionDisplay.risk.${tone}.copy`);
    return { label: this.lang.translate(`home.decisionDisplay.risk.${tone}.label`), summary: this.ensureSentence(summary), tone };
  }

  getConfidenceDisplay(confidence: DecisionConfidenceCompact): DecisionConfidenceDisplay {
    const percentage = Math.max(0, Math.min(100, Math.round((confidence.overall ?? 0) * 100)));
    const tone: DecisionConfidenceTone = percentage >= 85 ? "high" : percentage >= 70 ? "medium" : "low";
    return {
      label: this.lang.translate(`home.decisionDisplay.confidence.${tone}.label`),
      summary: this.lang.translate(`home.decisionDisplay.confidence.${tone}.copy`),
      percentage, percentageLabel: `${percentage}%`, tone
    };
  }

  getMissingInformationDisplay(item: DecisionMissingInformation): DecisionMissingInformationDisplay {
    const resolutionLocation = this.getMissingInformationResolutionLocation(item);
    return {
      label: item.label || this.humanizeFieldName(item.key) || this.lang.translate("home.decisionDetail.notAvailable"),
      blockingReason: this.normalizeUserFacingReason(item.reason) ?? this.lang.translate("home.decisionActionUx.missingInfoReason"),
      resolutionLocation,
      resolutionLocationLabel: this.lang.translate(`home.decisionDetail.missingLocation.${resolutionLocation}`),
      resolutionCopy: this.getResolutionCopy(resolutionLocation),
      externalResolutionCopy: this.lang.translate("home.decisionDetail.missingExternal"),
      nextStepCopy: this.lang.translate("home.decisionDetail.missingNextStep")
    };
  }

  getRiskTone(risk: DecisionRiskCompact, state: DecisionState): DecisionRiskTone {
    const level = (risk.level ?? "").trim().toLowerCase();
    if (level === "critical" || level === "high") return level;
    if (level === "medium" || state === "act_soon" || state === "waiting") return "medium";
    return level === "low" ? "low" : "none";
  }
  getResolutionCopy(location: MissingInformationResolutionLocation): string {
    return this.lang.translate(`home.decisionDetail.missingLocationCopy.${location}`);
  }
  normalizeUserFacingReason(reason?: string | null): string | null {
    if (!reason) return null;
    const cleaned = reason.replace(/\s+/g, " ").replace(/[;|]+/g, ". ").trim();
    if (!cleaned) return null;
    return this.ensureSentence(cleaned.split(/(?<=[.!?])\s+/)[0]?.trim() ?? cleaned);
  }
  looksTechnical(value: string): boolean {
    return /(boost|rule_|rule |source|catalog|trace|confidence source|db_|keyword_rule|deterministic|payload|engine)/
      .test(value.toLowerCase());
  }
  ensureSentence(value: string): string { return /[.!?]$/.test(value) ? value : `${value}.`; }
  humanizeFieldName(value?: string | null): string | null {
    if (!value) return null;
    const cleaned = value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_.-]+/g, " ").replace(/\s+/g, " ").trim();
    return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : null;
  }

  private getMissingInformationResolutionLocation(item: DecisionMissingInformation): MissingInformationResolutionLocation {
    if (item.resolutionLocation) return item.resolutionLocation;
    const text = `${item.key ?? ""} ${item.label ?? ""} ${item.reason ?? ""}`.toLowerCase();
    if (/(classification|classif|ocr|extract|label|category|tipo)/.test(text)) return "classification";
    if (/(system|erp|crm|sap|connector|catalog|source system|sistema)/.test(text)) return "systemOrigin";
    if (/(document|invoice|contract|source document|documento|archivo|form)/.test(text)) return "documentOrigin";
    return item.suggestedUserInput ? "manualReview" : "undetermined";
  }
}
